import {
  autoChangeKeepToEditForDeeplink,
  determineEngineMode,
  getAnswerableQuestionsAndPrune,
  getLinkIdOfQuestionnaireQuestion,
  resolveObservationAnchorDeeplink,
} from '../utils/questionnaire-engine.utils'
import { validateQuestionnaireAnswers } from '../utils/questionnaire-evaluation.utils'
import {
  addHealthParametersToAnswersOnAnswer,
  addHealthParametersToAnswersOnInitialize,
  buildHealthParameterDependencyMap,
} from '../utils/questionnaire-health-parameters.utils'
import {
  buildNavigationHistory,
  buildReconfirmNavigationHistory,
  clampHistoryToAnswerable,
  clampNavigationHistoryCursor,
  pushLinkIdToNavigationHistory,
  replaceLinkIdInNavigationHistory,
} from '../utils/questionnaire-navigation.utils'

import type { IQuestionnaireQuestion, QuestionAnswerInput, SubjectMember } from '@healthblocks-io/core'
import type {
  QuestionnaireAnswerWithFiles,
  QuestionnaireCareplanActivityWithIncludes,
} from '../types/questionnaire.types'
import type { QuestionnaireEngineState } from '../types/questionnaire-engine.types'

type QuestionnaireEngineAction =
  | {
      type: 'INITIALIZE'
      payload: {
        activity: QuestionnaireCareplanActivityWithIncludes
        initialLinkId?: string
        subject: SubjectMember
      }
    }
  | {
      type: 'SET_ANSWER'
      payload: {
        answer: QuestionAnswerInput & { fileIds: number[] }
        subject: SubjectMember
      }
    }
  | {
      type: 'GO_BACK'
    }
  | {
      type: 'NAVIGATE'
      payload: {
        linkId: string
        behavior: 'push' | 'replace'
      }
    }
  | {
      type: 'RESET_TO_LINK_ID'
      payload: { linkId: string }
    }

export function questionnaireEngineReducer(
  state: QuestionnaireEngineState,
  action: QuestionnaireEngineAction,
): QuestionnaireEngineState {
  switch (action.type) {
    case 'INITIALIZE': {
      return buildInitializeState({
        activity: action.payload.activity,
        subject: action.payload.subject,
        initialLinkId: action.payload.initialLinkId,
      })
    }
    case 'SET_ANSWER': {
      return buildSetAnswerState({
        state,
        answer: action.payload.answer,
        subject: action.payload.subject,
      })
    }

    case 'GO_BACK': {
      if (state.navigationHistory.length === 0) return state
      return {
        ...state,
        historyCursor: clampNavigationHistoryCursor(state.historyCursor - 1, state.navigationHistory.length),
      }
    }

    case 'NAVIGATE': {
      const { linkId, behavior } = action.payload

      const next =
        behavior === 'push'
          ? pushLinkIdToNavigationHistory(state.navigationHistory, state.historyCursor, linkId)
          : replaceLinkIdInNavigationHistory(state.navigationHistory, state.historyCursor, linkId)

      return {
        ...state,
        navigationHistory: next.history,
        historyCursor: next.cursor,
      }
    }
    case 'RESET_TO_LINK_ID': {
      const built = buildHistoryUpToLinkId(state.answerableQuestions, action.payload.linkId)
      if (!built) return state

      return {
        ...state,
        navigationHistory: built.history,
        historyCursor: built.cursor,
      }
    }
    default: {
      return state
    }
  }
}

/**
 * Build the initial state of the questionnaire engine
 * @param activity - The activity to build the state for
 * @param subject - The subject to build the state for
 * @param initialLinkId - The initial linkId to build the state for
 * @returns The initial state of the questionnaire engine
 */
function buildInitializeState({
  activity,
  subject,
  initialLinkId,
}: {
  activity: any
  subject: any
  initialLinkId?: string
}): QuestionnaireEngineState {
  // Step 1: Determine the mode
  const mode = determineEngineMode(activity)

  // Step 2: Load the initial answers
  let initialAnswers: Record<string, QuestionAnswerInput> = Object.fromEntries(
    activity.questionnaireResponse?.answers?.map((a: QuestionnaireAnswerWithFiles) => [a.linkId, a]) ?? [],
  )

  // Step 2.0.5: For reconfirm mode deeplinks, handle observation questions.
  // This must happen BEFORE skip logic so observation questions become answerable.
  if (mode === 'reconfirm' && initialLinkId) {
    // If deeplinked to an Observation anchor, auto-set to 'edit' and redirect
    // to the first dependent. The pencil click already expresses edit intent.
    const anchorRedirect = resolveObservationAnchorDeeplink(activity.questionnaire, initialAnswers, initialLinkId)
    if (anchorRedirect) {
      initialAnswers = anchorRedirect.answers
      initialLinkId = anchorRedirect.targetLinkId
    } else {
      // Handle deeplinks to dependent questions (existing logic)
      initialAnswers = autoChangeKeepToEditForDeeplink(activity.questionnaire, initialAnswers, initialLinkId)
    }
  }

  // Step 2.1: Add health parameters for any existing 'keep' answers BEFORE skip logic
  initialAnswers = addHealthParametersToAnswersOnInitialize(initialAnswers, activity.questionnaire)

  // Step 2.2: Calculate skip logic and get answerable questions
  // Health parameter answers will be preserved even if questions are skipped
  const { answerableQuestions, answers } = getAnswerableQuestionsAndPrune(
    activity.questionnaire,
    initialAnswers,
    subject,
  )

  const validations = validateQuestionnaireAnswers(answerableQuestions, answers, subject)

  if (answerableQuestions.length === 0) {
    return {
      status: 'in-progress',
      mode,
      questionnaire: activity.questionnaire,
      answerableQuestions,
      answers,
      validations,
      navigationHistory: [],
      historyCursor: 0,
      initialDeeplinkQuestion: initialLinkId,
    }
  }

  let navigationHistory: string[]
  let historyCursor: number

  if (mode === 'reconfirm') {
    const nav = buildReconfirmNavigationHistory(answerableQuestions, validations, initialLinkId)
    navigationHistory = nav.navigationHistory
    historyCursor = nav.historyCursor
  } else {
    const nav = buildNavigationHistory(answerableQuestions, answers, initialLinkId)
    navigationHistory = nav.navigationHistory
    historyCursor = nav.historyCursor

    // In edit mode, check if there's an invalid question before the current
    // navigation position. This handles stale invalid answers from previous
    // sessions (e.g. a required MultiSelect with value "").
    if (mode === 'edit' && !initialLinkId) {
      const firstInvalidLinkId = findFirstInvalidLinkId(answerableQuestions, validations)
      if (firstInvalidLinkId) {
        const invalidIndex = answerableQuestions.findIndex(
          (q) => getLinkIdOfQuestionnaireQuestion(q) === firstInvalidLinkId,
        )
        const currentLinkId = navigationHistory[historyCursor]
        const currentIndex = answerableQuestions.findIndex((q) => getLinkIdOfQuestionnaireQuestion(q) === currentLinkId)
        if (invalidIndex !== -1 && invalidIndex < currentIndex) {
          const built = buildHistoryUpToLinkId(answerableQuestions, firstInvalidLinkId)
          if (built) {
            navigationHistory = built.history
            historyCursor = built.cursor
          }
        }
      }
    }
  }

  return {
    status: 'in-progress',
    mode,
    questionnaire: activity.questionnaire,
    answerableQuestions,
    answers,
    validations,
    navigationHistory,
    historyCursor,
    initialDeeplinkQuestion: initialLinkId,
  }
}

/**
 * Build the state of the questionnaire engine when an answer is set
 * @param state - The current state of the questionnaire engine
 * @param answer - The answer to set
 * @returns The new state of the questionnaire engine
 */
function buildSetAnswerState({
  state,
  answer,
  subject,
}: {
  state: QuestionnaireEngineState
  answer: QuestionAnswerInput
  subject: any
}): QuestionnaireEngineState {
  // Step 1: Update the answers
  let nextAnswers = { ...state.answers, [answer.linkId]: answer }

  // Step 1.0.5: In reconfirm mode, clear dependent health parameter answers
  // when an anchor question's answer changes
  if (state.mode === 'reconfirm') {
    const { anchorToDependents } = buildHealthParameterDependencyMap(state.questionnaire)
    const dependents = anchorToDependents.get(answer.linkId)
    if (dependents) {
      for (const depLinkId of dependents) {
        delete nextAnswers[depLinkId]
      }
    }
  }

  // Step 1.1: Add health parameters BEFORE skip logic (if 'keep' was selected)
  nextAnswers = addHealthParametersToAnswersOnAnswer(nextAnswers, state.questionnaire, answer.linkId)

  // Step 2: Calculate skip logic and get answerable questions
  // This also handles health parameters (keep/edit logic)
  const { answerableQuestions, answers } = getAnswerableQuestionsAndPrune(state.questionnaire, nextAnswers, subject)

  // Step 3: Calculate validations
  const validations = validateQuestionnaireAnswers(answerableQuestions, answers, subject)

  // Step 2: Clamp the history to the answerable questions
  const clamped = clampHistoryToAnswerable(state.navigationHistory, state.historyCursor, answerableQuestions)

  return {
    ...state,
    answers,
    answerableQuestions,
    validations,
    navigationHistory: clamped.history,
    historyCursor: clamped.cursor,
  }
}

/**
 * Find the first invalid question linkId from validation results
 * Skips Display questions since they don't have validations.
 */
function findFirstInvalidLinkId(
  answerableQuestions: IQuestionnaireQuestion[],
  validations: Record<string, { isValid: boolean }>,
): string | null {
  for (const question of answerableQuestions) {
    if (question.type === 'Display') continue
    const linkId = getLinkIdOfQuestionnaireQuestion(question)
    if (!validations[linkId]?.isValid) {
      return linkId
    }
  }
  return null
}

export function buildHistoryUpToLinkId(answerableQuestions: IQuestionnaireQuestion[], linkId: string) {
  const index = answerableQuestions.findIndex((q) => getLinkIdOfQuestionnaireQuestion(q) === linkId)
  if (index === -1) return null

  const history = answerableQuestions.slice(0, index + 1).map(getLinkIdOfQuestionnaireQuestion)

  return { history, cursor: history.length - 1 }
}
